// ---------------------------------------------------------------------------
// Enterprise module — Data Governance management page
// ---------------------------------------------------------------------------

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { StatusBadge } from '../components/StatusBadge';
import { LoadingState } from '../components/LoadingState';
import { ErrorState } from '../components/ErrorState';
import { DataTable, type Column } from '../components/DataTable';
import { useFormatters } from '../hooks/useFormatters';

// ---------------------------------------------------------------------------
// Local types (mirrors @noxverify/data-governance types for UI consumption)
// ---------------------------------------------------------------------------

interface RetentionPolicy {
  id: string;
  entityType: string;
  retentionPeriod: number;
  action: string;
  jurisdictions: string[];
}

interface LegalHold {
  id: string;
  name: string;
  reason: string;
  entityIds: string[];
  createdBy: string;
  createdAt: string;
  releasedAt: string | null;
  status: string;
}

interface DataExportRequest {
  id: string;
  requestedBy: string;
  entityId: string;
  entityType: string;
  format: string;
  status: string;
  exportUrl: string | null;
  expiresAt: string | null;
  createdAt: string;
}

// ---------------------------------------------------------------------------
// Table columns
// ---------------------------------------------------------------------------

function buildRetentionColumns(t: ReturnType<typeof useTranslation>['t']): Column<RetentionPolicy>[] {
  return [
    {
      key: 'entityType',
      header: t('dataGovernance.retention.columns.entityType'),
      render: (p) => (
        <span className="text-sm font-medium text-ink">{p.entityType}</span>
      ),
    },
    {
      key: 'retentionPeriod',
      header: t('dataGovernance.retention.columns.period'),
      render: (p) => (
        <span className="text-sm text-ink">
          {t('dataGovernance.retention.periodFormat', {
            years: Math.round(p.retentionPeriod / 365),
            days: p.retentionPeriod,
          })}
        </span>
      ),
    },
    {
      key: 'action',
      header: t('dataGovernance.retention.columns.action'),
      render: (p) => <StatusBadge value={p.action} />,
    },
    {
      key: 'jurisdictions',
      header: t('dataGovernance.retention.columns.jurisdictions'),
      render: (p) => (
        <span className="text-sm text-ink-muted">
          {p.jurisdictions.length > 0
            ? p.jurisdictions.join(', ')
            : t('dataGovernance.retention.columns.jurisdictionsAll')}
        </span>
      ),
    },
  ];
}

function buildHoldColumns(
  t: ReturnType<typeof useTranslation>['t'],
  f: ReturnType<typeof useFormatters>,
): Column<LegalHold>[] {
  return [
    {
      key: 'id',
      header: t('dataGovernance.holds.columns.id'),
      render: (h) => <span className="text-sm font-mono text-ink">{h.id}</span>,
    },
    {
      key: 'name',
      header: t('dataGovernance.holds.columns.name'),
      render: (h) => <span className="text-sm font-medium text-ink">{h.name}</span>,
    },
    {
      key: 'reason',
      header: t('dataGovernance.holds.columns.reason'),
      render: (h) => (
        <span className="text-sm text-ink-muted truncate max-w-xs block">{h.reason}</span>
      ),
    },
    {
      key: 'entityIds',
      header: t('dataGovernance.holds.columns.entities'),
      render: (h) => <span className="text-sm text-ink">{h.entityIds.length}</span>,
    },
    {
      key: 'status',
      header: t('dataGovernance.holds.columns.status'),
      render: (h) => <StatusBadge value={h.status} />,
    },
    {
      key: 'createdAt',
      header: t('dataGovernance.holds.columns.created'),
      render: (h) => (
        <span className="text-sm text-ink-muted tabular-nums font-mono">
          {f.date(h.createdAt, { dateStyle: 'medium', timeStyle: 'short' })}
        </span>
      ),
    },
  ];
}

function buildExportColumns(
  t: ReturnType<typeof useTranslation>['t'],
  f: ReturnType<typeof useFormatters>,
): Column<DataExportRequest>[] {
  return [
    {
      key: 'id',
      header: t('dataGovernance.exports.columns.id'),
      render: (r) => <span className="text-sm font-mono text-ink">{r.id}</span>,
    },
    {
      key: 'entityId',
      header: t('dataGovernance.exports.columns.entity'),
      render: (r) => <span className="text-sm font-mono text-ink-muted">{r.entityId}</span>,
    },
    {
      key: 'entityType',
      header: t('dataGovernance.exports.columns.type'),
      render: (r) => <span className="text-sm text-ink">{r.entityType}</span>,
    },
    {
      key: 'format',
      header: t('dataGovernance.exports.columns.format'),
      render: (r) => <span className="text-sm text-ink uppercase">{r.format}</span>,
    },
    {
      key: 'status',
      header: t('dataGovernance.exports.columns.status'),
      render: (r) => <StatusBadge value={r.status} />,
    },
    {
      key: 'createdAt',
      header: t('dataGovernance.exports.columns.requested'),
      render: (r) => (
        <span className="text-sm text-ink-muted tabular-nums font-mono">
          {f.date(r.createdAt, { dateStyle: 'medium', timeStyle: 'short' })}
        </span>
      ),
    },
    {
      key: 'exportUrl',
      header: t('dataGovernance.exports.columns.download'),
      render: (r) =>
        r.exportUrl ? (
          <a
            href={r.exportUrl}
            className="text-sm text-primary hover:underline"
            target="_blank"
            rel="noopener noreferrer"
          >
            {t('dataGovernance.exports.downloadLink')}
          </a>
        ) : (
          <span className="text-sm text-ink-muted">--</span>
        ),
    },
  ];
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const TAB_KEYS = ['retention', 'holds', 'exports', 'anonymization'] as const;
type TabKey = (typeof TAB_KEYS)[number];

export function DataGovernancePage() {
  const { t } = useTranslation('admin');
  const f = useFormatters();
  const retentionColumns = useMemo(() => buildRetentionColumns(t), [t]);
  const holdColumns = useMemo(() => buildHoldColumns(t, f), [t, f]);
  const exportColumns = useMemo(() => buildExportColumns(t, f), [t, f]);
  const [policies, setPolicies] = useState<RetentionPolicy[]>([]);
  const [holds, setHolds] = useState<LegalHold[]>([]);
  const [exports, setExports] = useState<DataExportRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<TabKey>('retention');

  const loadData = useCallback(() => {
    setLoading(true);
    setError(null);

    // In production this would call the API endpoints
    Promise.resolve()
      .then(() => {
        setPolicies([]);
        setHolds([]);
        setExports([]);
      })
      .catch((err: unknown) => {
        const message =
          err instanceof Error ? err.message : t('dataGovernance.errors.loadFailed');
        setError(message);
      })
      .finally(() => setLoading(false));
  }, [t]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  if (loading) return <LoadingState rows={4} />;
  if (error) return <ErrorState message={error} onRetry={loadData} />;

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <h1 className="font-display text-3xl text-ink">{t('dataGovernance.headings.title')}</h1>
      </div>

      {/* Tab navigation */}
      <div className="border-b border-hairline mb-6">
        <nav className="flex space-x-8" aria-label={t('dataGovernance.tabsAriaLabel')}>
          {TAB_KEYS.map((tab) => (
            <button
              key={tab}
              type="button"
              onClick={() => setActiveTab(tab)}
              className={`py-2 px-1 border-b-2 text-sm font-medium ${
                activeTab === tab
                  ? 'border-primary text-primary'
                  : 'border-transparent text-ink-muted hover:text-ink hover:border-hairline-strong'
              }`}
            >
              {t(`dataGovernance.tabs.${tab}` as const)}
            </button>
          ))}
        </nav>
      </div>

      {/* Retention tab */}
      {activeTab === 'retention' && (
        <div>
          <div className="mb-4">
            <p className="text-sm text-ink-muted">{t('dataGovernance.retention.description')}</p>
          </div>
          <DataTable
            columns={retentionColumns}
            data={policies}
            rowKey={(p) => p.id}
            emptyMessage={t('dataGovernance.retention.empty')}
            ariaLabel={t('dataGovernance.retention.ariaLabel')}
          />
        </div>
      )}

      {/* Legal Holds tab */}
      {activeTab === 'holds' && (
        <div>
          <div className="flex items-center justify-between mb-4">
            <p className="text-sm text-ink-muted">{t('dataGovernance.holds.description')}</p>
            <button
              type="button"
              className="px-4 py-2 bg-primary text-white text-sm font-medium rounded hover:bg-primary-hover transition-colors"
              onClick={() => {
                // In production: open create legal hold modal
              }}
            >
              {t('dataGovernance.holds.create')}
            </button>
          </div>
          <DataTable
            columns={holdColumns}
            data={holds}
            rowKey={(h) => h.id}
            emptyMessage={t('dataGovernance.holds.empty')}
            ariaLabel={t('dataGovernance.holds.ariaLabel')}
          />
        </div>
      )}

      {/* Data Exports tab */}
      {activeTab === 'exports' && (
        <div>
          <div className="flex items-center justify-between mb-4">
            <p className="text-sm text-ink-muted">{t('dataGovernance.exports.description')}</p>
            <button
              type="button"
              className="px-4 py-2 bg-primary text-white text-sm font-medium rounded hover:bg-primary-hover transition-colors"
              onClick={() => {
                // In production: open create export request modal
              }}
            >
              {t('dataGovernance.exports.newExport')}
            </button>
          </div>
          <DataTable
            columns={exportColumns}
            data={exports}
            rowKey={(r) => r.id}
            emptyMessage={t('dataGovernance.exports.empty')}
            ariaLabel={t('dataGovernance.exports.ariaLabel')}
          />
        </div>
      )}

      {/* Anonymization tab */}
      {activeTab === 'anonymization' && (
        <div>
          <p className="text-sm text-ink-muted mb-4">{t('dataGovernance.anonymization.description')}</p>
          <div className="bg-surface-sunken border border-hairline rounded-sm p-6 text-center">
            <p className="text-ink-muted">{t('dataGovernance.anonymization.placeholder')}</p>
          </div>
        </div>
      )}
    </div>
  );
}
